import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
} from '@mui/material';
import { Phone, Email, LocationOn, HelpOutline, Send } from '@mui/icons-material';
import { contactService, newsletterService } from '../services/api';
import { ASSETS } from '../utils/assets';
import SimplePageBanner from '../components/SimplePageBanner';
import SiteStatsItems from '../components/SiteStatsItems';

const DARK_CHARCOAL = '#2D2F2D';
const BRAND_GREEN = '#477C2E';
const BRAND_ORANGE = '#E85D1C';
const DARK_TEAL = '#0f766e';
const TEAL_ICON = '#0d9488';
const CONTACT = {
  addressLine1: '15 Maniel Lane, Front Line',
  addressLine2: 'Berlin, Germany',
  phone: '9420249494',
  phoneLine1: '(+58-125) 25-3158',
  phoneLine2: '+163-524-4521',
  phoneDisplay: '+88 0123 654 99',
  email: 'info@rwfngo.com',
  questionsLine1: 'Discover more by visiting us',
  questionsLine2: 'or joining our community',
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState({ type: '', message: '' });
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await contactService.submitInquiry({ ...formData, subject: 'Contact form' });
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      alert('Error submitting inquiry');
    } finally {
      setLoading(false);
    }
  };

  const handleNewsletter = async (e) => {
    e.preventDefault();
    const value = newsletterEmail.trim();
    if (!value) {
      setNewsletterStatus({ type: 'error', message: 'Please enter your email address.' });
      return;
    }
    try {
      setNewsletterSubmitting(true);
      setNewsletterStatus({ type: '', message: '' });
      const res = await newsletterService.subscribe(value, 'contact');
      setNewsletterStatus({
        type: 'success',
        message: res?.data?.message || 'Thank you for subscribing to our newsletter.',
      });
      setNewsletterEmail('');
    } catch (error) {
      setNewsletterStatus({
        type: 'error',
        message: error.response?.data?.error || 'Unable to subscribe. Please try again.',
      });
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  const mapEmbedUrl = 'https://www.openstreetmap.org/export/embed.html?bbox=13.3765%2C52.5163%2C13.4315%2C52.5295&layer=mapnik&marker=52.5229%2C13.4040';

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <SimplePageBanner
        subtitle="Get in touch"
        title="Contact Us"
        description="Reach out with questions, partnerships, or support—we are here to help."
      />

      {/* Contact Information & Map - white card, left ~40% contact blocks with rounded-square icons, right ~60% map with gutter */}
      <Box sx={{ backgroundColor: '#FAF9F6', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg" sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
              p: { xs: 3, md: 4 },
            }}
          >
            <Grid container spacing={4}>
              <Grid item xs={12} md={5}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                  <Box sx={{ width: 52, height: 52, borderRadius: 2, backgroundColor: TEAL_ICON, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mr: 2 }}>
                    <LocationOn sx={{ fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: 14, color: '#2D2929', mb: 0.5 }}>Address</Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 14, color: '#4B4646', lineHeight: 1.6 }}>{CONTACT.addressLine1}</Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 14, color: '#4B4646', lineHeight: 1.6 }}>{CONTACT.addressLine2}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                  <Box sx={{ width: 52, height: 52, borderRadius: 2, backgroundColor: '#E9A23B', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mr: 2 }}>
                    <Phone sx={{ fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: 14, color: '#2D2929', mb: 0.5 }}>Phone</Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 14, color: '#4B4646', lineHeight: 1.6 }}>{CONTACT.phoneLine1}</Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 14, color: '#4B4646', lineHeight: 1.6 }}>{CONTACT.phoneLine2}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                  <Box sx={{ width: 52, height: 52, borderRadius: 2, backgroundColor: DARK_CHARCOAL, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mr: 2 }}>
                    <Email sx={{ fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: 14, color: '#2D2929', mb: 0.5 }}>Email</Typography>
                    <Typography
                      component="a"
                      href={`mailto:${CONTACT.email}`}
                      sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 14, color: '#4B4646', lineHeight: 1.6, textDecoration: 'none', display: 'block' }}
                    >
                      {CONTACT.email}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box sx={{ width: 52, height: 52, borderRadius: 2, backgroundColor: BRAND_ORANGE, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mr: 2 }}>
                    <HelpOutline sx={{ fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: 14, color: '#2D2929', mb: 0.5 }}>Have Questions?</Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 14, color: '#4B4646', lineHeight: 1.6 }}>{CONTACT.questionsLine1}</Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 14, color: '#4B4646', lineHeight: 1.6 }}>{CONTACT.questionsLine2}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={7} sx={{ position: 'relative', minHeight: { xs: 280, md: 360 } }}>
                <Box
                  component="a"
                  href="https://www.google.com/maps"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    zIndex: 2,
                    backgroundColor: '#fff',
                    color: DARK_CHARCOAL,
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: 600,
                    fontSize: 13,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    textDecoration: 'none',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                  }}
                >
                  Open in Maps
                </Box>
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    height: '100%',
                    minHeight: 360,
                    filter: 'grayscale(1)',
                  }}
                >
                  <Box
                    component="iframe"
                    src={mapEmbedUrl}
                    title="Map"
                    sx={{
                      width: '100%',
                      height: '100%',
                      minHeight: 360,
                      border: 0,
                      display: 'block',
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Contact Form - equal columns: left image with teal/orange splashes + oval shadow, right form card */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg" sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Grid container alignItems="center" spacing={{ xs: 0, md: 8 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', minHeight: { xs: 320, md: 440 } }}>
                <Box
                  sx={{
                    position: 'absolute',
                    left: { xs: -20, md: -40 },
                    bottom: '15%',
                    width: { xs: 120, md: 180 },
                    height: { xs: 120, md: 180 },
                    borderRadius: '50%',
                    background: `radial-gradient(circle at 30% 30%, ${DARK_TEAL}50 0%, ${DARK_TEAL}25 50%, transparent 70%)`,
                    zIndex: 0,
                    pointerEvents: 'none',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    right: { xs: -20, md: -40 },
                    top: '20%',
                    width: { xs: 100, md: 160 },
                    height: { xs: 100, md: 160 },
                    borderRadius: '50%',
                    background: `radial-gradient(circle at 70% 70%, ${BRAND_ORANGE}45 0%, ${BRAND_ORANGE}20 50%, transparent 70%)`,
                    zIndex: 0,
                    pointerEvents: 'none',
                  }}
                />
                <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 420 }}>
                  <Box
                    component="img"
                    src={ASSETS.parallax}
                    alt="Donation"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      minHeight: { xs: 280, md: 380 },
                      objectFit: 'cover',
                      borderRadius: 4,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      display: 'block',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -24,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '80%',
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      filter: 'blur(8px)',
                      zIndex: 0,
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: 5,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                  p: { xs: 3, md: 4 },
                  border: '1px solid rgba(0,0,0,0.06)',
                }}
              >
                {submitted ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: 20, color: BRAND_GREEN, mb: 2 }}>Message Sent Successfully!</Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 14, color: '#4B4646' }}>Thank you for contacting us. We'll get back to you as soon as possible.</Typography>
                  </Box>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      placeholder="Your Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: '#fff',
                          '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      placeholder="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: '#fff',
                          '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      placeholder="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: '#fff',
                          '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      placeholder="Type Your Message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      multiline
                      rows={5}
                      sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: '#fff',
                          '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
                        },
                      }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      disableElevation
                      disabled={loading}
                      sx={{
                        height: 52,
                        borderRadius: 9999,
                        backgroundColor: '#1D6354',
                        color: '#fff',
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 600,
                        fontSize: 15,
                        px: 4,
                        '&:hover': { backgroundColor: '#165a4c' },
                      }}
                    >
                      {loading ? 'Sending...' : 'Send a Message'}
                    </Button>
                  </form>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Newsletter bar - dark section: left-aligned text, right = pill input + orange send button */}
      <Box sx={{ backgroundColor: DARK_CHARCOAL, py: { xs: 4, md: 5 } }}>
        <Container maxWidth="lg" sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
            }}
          >
            <Box sx={{ textAlign: { xs: 'center', md: 'left' }, flex: { md: '0 0 auto' } }}>
              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: { xs: 22, md: 24 }, color: '#FFFFFF', mb: 0.5 }}>
                Subscribe To Our Newsletter
              </Typography>
              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 400, fontSize: 14, color: 'rgba(255,255,255,0.7)' }}>
                Regular inspections and feedback mechanisms.
              </Typography>
            </Box>
            <Box sx={{ width: { xs: '100%', md: 'auto' }, display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-end' } }}>
            <Box component="form" onSubmit={handleNewsletter} sx={{ display: 'flex', alignItems: 'stretch', gap: 0, width: { xs: '100%', md: 560 }, maxWidth: { xs: 400, md: 560 }, flex: { xs: '1 1 auto', md: '0 0 auto' } }}>
              <TextField
                size="small"
                placeholder="Enter Email Address"
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                disabled={newsletterSubmitting}
                sx={{
                  flex: 1,
                  minWidth: { xs: 0, md: 500 },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    borderRadius: 9999,
                    height: 48,
                    pr: 0.5,
                    '& fieldset': { border: 'none' },
                  },
                }}
              />
              <Button
                type="submit"
                disabled={newsletterSubmitting}
                sx={{
                  width: 48,
                  height: 48,
                  minWidth: 48,
                  borderRadius: 9999,
                  backgroundColor: BRAND_ORANGE,
                  color: '#fff',
                  ml: 0.5,
                  '&:hover': { backgroundColor: '#d14e14' },
                }}
              >
                <Send sx={{ fontSize: 22 }} />
              </Button>
            </Box>
            {newsletterStatus.message && (
              <Typography sx={{ mt: 1.5, textAlign: { xs: 'center', md: 'right' }, fontSize: 13, color: newsletterStatus.type === 'error' ? '#ffb4a8' : '#b7f0c4' }}>
                {newsletterStatus.message}
              </Typography>
            )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Join With Us - stats bar (dark green overlay on image, 4 stats, script labels) */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Typography sx={{ fontFamily: '"Caveat", cursive', fontWeight: 700, fontSize: { xs: 24, md: 27 }, color: '#313530', textAlign: 'center', mb: '25px' }}>
            Join With Us
          </Typography>
          <Typography
            component="h2"
            sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: { xs: 26, sm: 34, md: 40 }, lineHeight: 1.25, letterSpacing: '0.01em', color: '#2D2929', textAlign: 'center', mb: { xs: '30px', md: '50px' } }}
          >
            Join in Roots & Wings
            <br />
            for Better Tomorrow
          </Typography>
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              maxWidth: 1200,
              mx: 'auto',
              borderRadius: { xs: 16, md: '23px' },
              minHeight: { xs: 200, md: 238 },
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #c4a574',
              backgroundImage: `url(${ASSETS.parallax})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              '&::before': { content: '""', position: 'absolute', inset: 0, backgroundColor: 'rgba(36, 88, 78, 0.63)', borderRadius: '23px' },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                justifyContent: 'space-evenly',
                alignItems: 'center',
                flex: 1,
                minHeight: { xs: 200, md: 238 },
                gap: { xs: 3, sm: 0 },
                py: { xs: 4, md: 5 },
                px: { xs: 3, md: 4 },
              }}
            >
              <SiteStatsItems />
            </Box>
          </Box>
        </Container>
      </Box>

    </Box>
  );
};

export default ContactPage;
